import { bbox } from "@turf/turf";
import {
  Feature,
  MultiPolygon as GeoJsonMultiPolygon,
  Polygon as GeoJsonPolygon,
} from "geojson";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  GeolocateControl,
  Layer,
  LngLatBoundsLike,
  Map,
  MapRef,
  NavigationControl,
  ScaleControl,
  Source,
} from "react-map-gl/maplibre";
import { mapTransformRequest } from "../../API/AureaApi";
import { getUrl } from "../../getUrl";
import { FieldProperties, FieldsGeoJSON, Tiling } from "../../API/fields";
import { mapStyle } from "../Map";
import Popup from "../Map/Popup";
import useLayerEvent from "../Map/useLayerEvent";
import "./FieldMap.module.css";
import styles from "./FieldMap.module.css";

export type FieldFeature = Feature<
  GeoJsonMultiPolygon | GeoJsonPolygon,
  FieldProperties
>;

export type FieldMapProps = {
  geojson: FieldsGeoJSON;
  selectedFieldIds?: number[];
  tiling?: Tiling | null;
  onClick: (feature: FieldFeature) => void;
  children?: React.ReactNode;
  events?: React.UIEvent;
};

export default function FieldMap({
  geojson,
  selectedFieldIds,
  onClick,
  children,
  tiling,
}: FieldMapProps): React.ReactNode {
  const ref = useRef<MapRef | null>(null);

  const selectedFields = geojson.features.filter((field) =>
    selectedFieldIds?.includes(field.properties.id),
  );

  const bounds: LngLatBoundsLike =
    selectedFields.length > 0
      ? (bbox({
          type: "FeatureCollection",
          features: selectedFields,
        }) as LngLatBoundsLike)
      : geojson.features.length
        ? (bbox(geojson) as LngLatBoundsLike)
        : [-10, 33, 30, 60];

  useEffect(() => {
    if (!ref.current) return;
    const target =
      selectedFields.length > 0
        ? { type: "FeatureCollection" as const, features: selectedFields }
        : geojson.features.length > 0
          ? geojson
          : null;
    if (!target) return;
    ref.current.fitBounds(bbox(target) as LngLatBoundsLike, {
      padding: 40,
      maxZoom: 17,
      duration: 500,
    });
  }, [selectedFieldIds, ref]);

  return (
    <div className={styles.fieldOverview}>
      <Map
        mapStyle={mapStyle}
        transformRequest={mapTransformRequest}
        initialViewState={{
          bounds,
          fitBoundsOptions: {
            maxZoom: 17,
          },
        }}
        ref={ref}
        maxZoom={25}
      >
        <FieldLayer
          selectedFieldIds={selectedFieldIds}
          onClick={onClick}
          fields={geojson}
        />
        <PhotoLayer photo={tiling} />
        <ScaleControl position="bottom-right" />
        <NavigationControl position="bottom-right" />
        <GeolocateControl
          position="bottom-right"
          trackUserLocation={true}
          showUserLocation={true}
          showAccuracyCircle={true}
          positionOptions={{
            enableHighAccuracy: true,
            timeout: 30000,
            maximumAge: 0,
          }}
          // Error codes:
          // 1 = PERMISSION_DENIED
          // 2 = POSITION_UNAVAILABLE
          // 3 = TIMEOUT
          onError={(e) => console.error("Geolocation error", e.code, e.message)}
        />
        {children}
      </Map>
    </div>
  );
}

type PhotoLayerProps = { photo: Tiling | null | undefined };
function PhotoLayer({ photo }: PhotoLayerProps) {
  return (
    photo && (
      <Source
        key={photo.id}
        type="raster"
        tiles={[getUrl(`/v1/tiling/${photo.id}/tile/{z}/{x}/{y}`)]}
        scheme="tms"
        tileSize={256}
        maxzoom={photo.max_zoom_level || 22}
      >
        <Layer type="raster" beforeId="tiling" />
      </Source>
    )
  );
}

type FieldProps = {
  fields: FieldsGeoJSON;
  selectedFieldIds?: number[];
  onClick: FieldMapProps["onClick"];
};

function FieldLayer({
  fields,
  selectedFieldIds,
  onClick,
}: FieldProps): React.ReactNode {
  const rootStyle = getComputedStyle(document.documentElement);

  useLayerEvent(
    "click",
    "fields-fill",
    /* istanbul ignore next */ (event) => {
      if (event?.features?.length) {
        onClick(event.features[0] as unknown as FieldFeature);
      }
    },
  );

  return (
    <>
      <Source type="geojson" data={fields}>
        <Layer
          id="fields-fill"
          type="fill"
          paint={{
            "fill-color": rootStyle.getPropertyValue("--aurea-normal"),
            "fill-opacity": 0.3,
          }}
          filter={[
            "!",
            ["in", ["get", "id"], ["literal", selectedFieldIds || []]],
          ]}
          beforeId="field"
        />
        <Layer
          type="line"
          paint={{
            "line-color": [
              "case",
              ["in", ["get", "id"], ["literal", selectedFieldIds || []]],
              rootStyle.getPropertyValue("--aurea-highlight"),
              rootStyle.getPropertyValue("--aurea-normal"),
            ],
            "line-width": 3,
          }}
          beforeId="fields-fill"
        />
      </Source>
      <Popup layer="fields-fill" component={FieldInformation} />
    </>
  );
}

function FieldInformation({
  properties,
}: {
  properties: FieldProperties;
}): React.ReactNode {
  const { t } = useTranslation();

  return (
    <>
      <strong className={styles.fieldItemTitle}>{properties.name}</strong>
      <span className={styles.fixedWidthArea}>
        {t("text.{{area}}.ha", {
          area: properties.area_ha.toFixed(2),
        })}
      </span>
      <span className={styles.fixedVariety}>{properties.variety}</span>
    </>
  );
}
